import React, { useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { ProcedureOption } from "./SetupWizard";
import { ProcedureKey, procedures } from "../../data/procedures";

interface ProcedureSelectionWizardProps {
  onNext: (selectedProcedure: ProcedureOption) => void;
  onCancel: () => void;
}

export const ProcedureSelectionWizard: React.FC<
  ProcedureSelectionWizardProps
> = ({ onNext, onCancel }) => {
  const [selectedProcedure, setSelectedProcedure] = useState<string>(
    ProcedureKey.LIFTING
  );

  const procedureOptions: ProcedureOption[] = [
    {
      key: ProcedureKey.LIFTING,
      isDefault: true,
    },
    {
      key: ProcedureKey.PERSONNEL,
    },
    {
      key: ProcedureKey.PARKING,
    },
    {
      key: ProcedureKey.MAINTENANCE,
    },
  ];

  const handleNext = () => {
    const selected = procedureOptions.find((p) => p.key === selectedProcedure);
    if (selected) {
      onNext(selected);
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center space-x-4 mb-8">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-crane-selection-blue rounded-full flex items-center justify-center">
            <span className="text-crane-white text-sm font-medium">1</span>
          </div>
          <span className="crane-text-body-lg text-crane-white font-medium">
            OPERATION
          </span>
        </div>

        <div className="flex-1 h-px bg-crane-darker"></div>

        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-crane-darker rounded-full flex items-center justify-center">
            <span className="text-crane-gray text-sm font-medium">2</span>
          </div>
          <span className="crane-text-body-lg text-crane-gray">HOISTS</span>
        </div>

        <div className="flex-1 h-px bg-crane-darker"></div>

        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-crane-darker rounded-full flex items-center justify-center">
            <span className="text-crane-gray text-sm font-medium">3</span>
          </div>
          <span className="crane-text-body-lg text-crane-gray">PARAMETERS</span>
        </div>
      </div>

      {/* Operation Type Selection */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <h3 className="crane-text-heading-lg">Offshore lift</h3>
          <ChevronDownIcon className="w-5 h-5 text-crane-gray" />
        </div>

        <p className="crane-text-body-lg text-crane-gray">
          GPS detected. Select different one if wrong.
        </p>
      </div>

      {/* Question and Description */}
      <div className="space-y-4">
        <h2 className="crane-text-heading-lg">
          What type of lift are you preparing for?
        </h2>
        <p className="crane-text-body-lg text-crane-gray">
          The crane will apply the right limits and behavior.
        </p>
      </div>

      {/* Procedure Options Grid */}
      <div
        className="grid grid-cols-4 gap-4 w-full min-h-0"
        // style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)" }}
      >
        {procedureOptions.map(({ key, isDefault }) => {
          const procedure = procedures[key];
          return (
            <div
              key={key}
              className={`relative p-6 rounded-lg border-2 cursor-pointer transition-colors min-h-[180px] flex flex-col ${
                selectedProcedure === key
                  ? "border-crane-selection-blue bg-crane-darker"
                  : "border-crane-darker bg-crane-surface hover:border-crane-slate"
              }`}
              onClick={() => setSelectedProcedure(key)}
            >
              {/* Icon Placeholder */}
              <div className="w-12 h-12 bg-crane-light-gray rounded-full mb-4 mx-auto"></div>

              {/* Title and Description */}
              <div className="text-center space-y-2 flex-1">
                <h4 className="crane-text-body-lg font-medium text-crane-white">
                  {procedure.name}
                </h4>
                <p className="crane-text-body-md text-crane-gray">
                  {procedure.description}
                </p>
              </div>

              {/* Selection Indicator */}
              <div className="flex justify-center mt-4">
                <div
                  className={`w-4 h-4 rounded-full border-2 ${
                    selectedProcedure === key
                      ? "border-crane-selection-green bg-crane-selection-green"
                      : "border-crane-gray"
                  }`}
                >
                  {selectedProcedure === key && (
                    <div className="w-2 h-2 bg-crane-white rounded-full m-0.5"></div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4 pt-6">
        <button onClick={onCancel} className="crane-button-secondary">
          Cancel
        </button>
        <button
          onClick={handleNext}
          className="crane-button-secondary flex items-center space-x-2"
        >
          <span>Next</span>
          <span>→</span>
        </button>
      </div>
    </div>
  );
};
